/**
 * Created by Jie Gao on 2018-11-08.
 */
import {IPickAndClickViewConfig, PickAndClickView} from "@nolimitcity/slot-game/bin/core/pickandclick/PickAndClickView";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Translation} from "@nolimitcity/slot-game/bin/core/translation/Translation";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Power3, TimelineLite, Tween, TweenLite} from "gsap";
import {ButtonState, StarStruckButton} from "../effects/StarStruckButton";
import {StarStruckParsedGameData} from "../server/data/StarStruckParsedGameData";
import {StarStruckGameConfig} from "../StarStruckGameConfig";
import {StarStruckGameModuleConfig} from "../StarStruckGameModuleConfig";
import {StarStruckSoundConfig} from "../StarStruckSoundConfig";
import {StarStruckReelSymbol} from "../symbols/StarStruckReelSymbol";
import BitmapText = PIXI.BitmapText;

export interface IMultiplierItem {
    star:StarStruckButton;
    text:BitmapText;
}

export class StarStruckPickNClickScreen extends PickAndClickView {

    private readonly R:number = 200;
    private _starsPopAnimation:TimelineLite | null;
    private _startPos:number[][] = [];
    private _endPos:number[][] = [];
    private _centerX:number;
    private _centerY:number;
    private _stars:IMultiplierItem[];
    private _bonusStarAnimation:TimelineSprite;
    private _starAnimation:TimelineSprite;
    private _shuffleAnimation:TimelineSprite;
    private _buttonsContainer:PIXI.Sprite;
    private _pickText:PIXI.Text;

    private _idleTimer:Tween | null;
    private _startTime:number = 8;
    private _minInterval:number = 8;
    private _maxInterval:number = 12;
    private _isPlaying:boolean = false;
    private _addDelay:boolean = false;

    constructor(config:IPickAndClickViewConfig, autoClick:boolean = false) {
        super(config, autoClick);
        const r = this.R;
        this._centerX = Helper.getSymbolPositions(1, 1)[0];
        this._centerY = Helper.getSymbolPositions(1, 1)[1];
        this._startPos = ArrayHelper.initArrayWithValues(8, (i:number) => {return [this._centerX, this._centerY];});
        this._endPos = [[0, 0 - r], [r * 0.7, 0 - r * 0.7], [r, 0], [r * 0.7, r * 0.7], [0, r], [0 - r * 0.7, r * 0.7], [0 - r, 0], [0 - r * 0.7, -r * 0.7]];
        this._buttons = [];
    }

    protected initAnimations():void {
        super.initAnimations();

        this._buttonsContainer = new PIXI.Sprite(GameResources.getTextures("starContainer")[0]);
        this._buttonsContainer.blendMode = PIXI.BLEND_MODES.ADD;
        this._buttonsContainer.anchor.set(0.5, 0.5);
        this.addChild(this._buttonsContainer);

        this._bonusStarAnimation = new TimelineSprite(GameResources.getTextures("bonusStar"));
        this._bonusStarAnimation.anchor.set(0.5, 0.5);
        this._bonusStarAnimation.scale.set(1.3, 1.3);
        this._bonusStarAnimation.position.set(Helper.getSymbolPositions(1, 1)[0], Helper.getSymbolPositions(1, 1)[1]);
        this._bonusStarAnimation.hide();
        this.addChild(this._bonusStarAnimation);

        this._starAnimation = new TimelineSprite(GameResources.getTextures("winStar"));
        this._starAnimation.blendMode = PIXI.BLEND_MODES.ADD;
        this._starAnimation.anchor.set(0.5, 0.5);
        this._starAnimation.position.set(Helper.getSymbolPositions(1, 1)[0], Helper.getSymbolPositions(1, 1)[1]);
        this._starAnimation.hide();
        this.addChild(this._starAnimation);

        this._shuffleAnimation = new TimelineSprite(GameResources.getTextures("bonusShuffle"));
        this._shuffleAnimation.anchor.set(0.5, 0.5);
        this._shuffleAnimation.position.set(Helper.getSymbolPositions(1, 1)[0], Helper.getSymbolPositions(1, 1)[1]);
        this._shuffleAnimation.hide();
        this.addChild(this._shuffleAnimation);

        const textStyle:any = {
            fontFamily : "Open Sans",
            fontSize : 40,
            fontWeight : "bold",
            fill : "#ffffff",
            dropShadow : true,
            dropShadowAngle : 8,
            dropShadowBlur : 10,
            dropShadowDistance : 4
        };

        this._pickText = new PIXI.Text(Translation.translate("Pick a star"), textStyle);
        this._pickText.anchor.set(0.5, 0.5);
        this._pickText.position.set(Helper.getSymbolPositions(1, 1)[0], Helper.getSymbolPositions(1, 1)[1]);
        if(this._pickText.width > 200) {
            this._pickText.width = 200
        }
        this._pickText.alpha = 0;
        this.addChild(this._pickText);

        this._stars = [];
    }

    protected createButton(id:number, multiplier:number, clickCallback:(id:number, auto:boolean) => void):StarStruckButton {
        const button = new StarStruckButton(
            {
                idle : GameResources.getTextures("B_pick")[0],
                over : GameResources.getTextures("B_pick")[0],
                down : GameResources.getTextures("B_pick")[0],
                deactive : GameResources.getTextures("B_pick_disabled")[0],
                disabled : GameResources.getTextures("B_pick")[0]
            },
            () => {
                clickCallback(id, this._autoClicked);
            });
        button.scale.set(0.5, 0.5);
        button.anchor.set(0.5, 0.5);
        button.position.set(0, 0);

        const text:BitmapText = new BitmapText(multiplier.toString(), {
            fontName : "NumbersWinCountUp",
            fontSize : 20
        });
        text.position.set(-text.width / 2, -text.height / 2);
        text.alpha = 0;
        button.addChild(text);
        this._stars.push({star : button, text : text});
        return button;
    }

    protected createButtons(buttonConfig:any[]):StarStruckButton[] {
        const buttons:StarStruckButton[] = [];
        buttonConfig.forEach((config:any, index:number) => {
            const button:StarStruckButton = this.createButton(index, config, this._onButtonClickCallback);
            button.enabled = false;
            buttons.push(button);
            this._buttonsContainer.addChild(button);
        });

        return buttons;
    }

    public show(buttonConfig:any[], data?:ParsedServerData, onShowComplete?:() => void):void {
        const tl:TimelineLite = new TimelineLite();
        tl.add(() => super.show(buttonConfig, data, onShowComplete), this._addDelay ? 1 : 0);
    }

    public startPresentation(data:StarStruckParsedGameData, onShowComplete:() => void):void {
        const tl:TimelineLite = new TimelineLite({paused : true});
        const tlStar:TimelineLite = new TimelineLite();
        const fs:number = 0.033;
        const texts:number[] = data.bonusValues;
        const x:number = 0;
        const y:number = 0;
        const pos:number[][] = this._endPos;
        this.getStarsPopAnimation();
        this._pickText.tint = 0xffffff;
        this._stars.forEach((multiplierItem:IMultiplierItem, i:number) => {
            const star:PIXI.Sprite = multiplierItem.star;
            const text:BitmapText = multiplierItem.text;
            const starTimeline:TimelineLite = new TimelineLite();

            text.maxWidth = 100;
            text.text = "X" + texts[i].toString();
            text.position.set(-text.width / 2, -text.height / 2);
            text.tint = 0xffffff;
            this._buttonsContainer.addChild(star);
            //Spread out
            starTimeline.add([
                TweenLite.fromTo(star.scale, 0, {x : 0.35, y : 0.35}, {x : 0.35, y : 0.35}),
                TweenLite.fromTo(star, 0, {alpha : 1}, {alpha : 1}),
                TweenLite.fromTo(star.scale, 4 * fs, {x : 0.35, y : 0.35}, {x : 0.8, y : 0.8}),
                TweenLite.fromTo(star, 8 * fs, {x : x, y : y}, {x : x + pos[i][0], y : y + pos[i][1]})
            ]);

            starTimeline.add(TweenLite.to(star.scale, 2 * fs, {x : 0.6, y : 0.6}));
            starTimeline.add(TweenLite.to(star.scale, 8 * fs, {x : 0.7, y : 0.7}));

            //blink
            starTimeline.add([
                () => {
                    text.alpha = 1;
                    SlotGame.sound.playEffect(StarStruckSoundConfig.instance.BONUS_STAR_POP);
                },
                TweenLite.to(star.scale, 2 * fs, {x : 1, y : 1})
            ], 18 * fs + i * 10 * fs);
            starTimeline.add(TweenLite.to(star.scale, 8 * fs, {x : 0.7, y : 0.7}), 20 * fs + i * 10 * fs);
            tlStar.add(starTimeline, 0);
        });
        tlStar.add(() => {SlotGame.sound.playEffect(StarStruckSoundConfig.instance.BONUS_STAR_OUT1);}, 0);
        tlStar.add(() => {
            SlotGame.sound.playEffect(StarStruckSoundConfig.instance.BONUS_STAR_SWEEP);
            this._starsPopAnimation!.timeScale(2);
            this._starsPopAnimation!.play(0);

        });
        tlStar.add(() => {}, `+=${this._starsPopAnimation!.duration() / 2}`);
        tl.add([
            () => {
                StarStruckGameModuleConfig.hotZone.hide(true);
                this._bonusStarAnimation.show();
                this._bonusStarAnimation.playLoop();
            },
            this._starAnimation.getAnimationAutoShowHide(true, true),
            tlStar
        ], 0);
        tl.add(this.showAllButtons());
        tl.add(() => {
            this._bonusStarAnimation.stopLoop();
            this._bonusStarAnimation.hide();
            onShowComplete();
        });
        StarStruckGameModuleConfig.hotZone.stopHotZoneWinStar();
        tl.play();
    }

    private getStarsPopAnimation():void {
        if(this._starsPopAnimation && this._starsPopAnimation.isActive()) {
            this._starsPopAnimation.pause();
        }
        this._starsPopAnimation = null;
        const fs:number = 0.033;
        this._starsPopAnimation = new TimelineLite({paused : true});
        this._stars.forEach((multiplierItem:IMultiplierItem, i:number) => {
            const star:PIXI.Sprite = multiplierItem.star;
            const temp:TimelineLite = new TimelineLite();
            temp.add([
                TweenLite.to(star.scale, 4 * fs, {x : 0.8, y : 0.8})
            ]);
            temp.add(TweenLite.to(star.scale, 2 * fs, {x : 0.6, y : 0.6}));
            temp.add(TweenLite.to(star.scale, 8 * fs + ((i === 7) ? 1 : 0), {x : 0.7, y : 0.7}));
            this._starsPopAnimation!.add(temp);
        });
    }

    public showAllButtons():TimelineLite {
        this._layer.addChild(this);
        const tl:TimelineLite = new TimelineLite();
        const fs:number = 0.033;
        const x:number = 0;
        const y:number = 0;
        const pos:number[][] = this._endPos;
        this.addChild(this._shuffleAnimation);
        const shuffleAnimation = new TimelineLite().add([
            () => {SlotGame.sound.playEffect(StarStruckSoundConfig.instance.BONUS_STAR_SHUFFLE);},
            this._shuffleAnimation.getAnimationAutoShowHide(true, true)
        ]);
        const shuffleAnimationDuration:number = shuffleAnimation.duration();
        this._stars.forEach((multiplierItem:IMultiplierItem, i:number) => {
            const star:StarStruckButton = multiplierItem.star;
            const text:BitmapText = multiplierItem.text;
            tl.add(new TimelineLite().add(
                [
                    () => {text.alpha = 0;},
                    TweenLite.to(multiplierItem.star.scale, 0, {x : 0.8, y : 0.8}),
                    TweenLite.to(star, 8 * fs, {x : x, y : y})
                ]), 0);
            tl.add([
                TweenLite.to(star, 8 * fs, {x : x + pos[i][0], y : y + pos[i][1]})
            ], 8 * fs + shuffleAnimationDuration);
            tl.add(TweenLite.to(star, 8 * fs, {alpha : 1}), (8 + 8) * fs + shuffleAnimationDuration);
        });

        tl.add(() => {SlotGame.sound.playEffect(StarStruckSoundConfig.instance.BONUS_STAR_IN);}, 0);
        tl.add([
            shuffleAnimation,
            () => {
                this._bonusStarAnimation.stopLoop();
                this._bonusStarAnimation.hide()
            }
        ], 8 * fs);
        tl.add([
            TweenLite.fromTo(this._pickText, 4 * fs, {alpha : 0}, {alpha : 1}),
            () => {
                SlotGame.sound.playEffect(StarStruckSoundConfig.instance.BONUS_STAR_OUT2);
                this.onIdle();
            }
        ], 8 * fs + shuffleAnimationDuration);

        return tl;
    }

    protected stopPresentation(data:StarStruckParsedGameData, closeCallback:() => void):void {
        const tl:TimelineLite = new TimelineLite({paused : true});
        const fs:number = 0.033;

        this.onIdleStop();
        const star:IMultiplierItem = this._stars[data.playerSelection];
        this._stars.forEach((multiplierItem:IMultiplierItem, i:number) => {
            const text:BitmapText = multiplierItem.text;
            text.maxWidth = 100;
            text.text = "X" + data.bonusValues[i].toString();
            text.position.set(-text.width / 2, -text.height / 2);
        });

        this._stars.forEach((multiplierItem:IMultiplierItem, i:number) => {
            const text:BitmapText = multiplierItem.text;

            tl.add([
                TweenLite.fromTo(text, 30 * fs, {alpha : 1}, {alpha : 1}),
                () => {
                    this._pickText.tint = 0x555555;
                    if(i !== data.playerSelection) {
                        multiplierItem.star.setState(ButtonState.deactive);
                        text.tint = 0x555555;
                    }
                }
            ], 0);
        });

        tl.add([
            () => {
                this._buttonsContainer.addChild(star.star);
                this._pickText.alpha = 0;
                SlotGame.sound.playEffect(StarStruckSoundConfig.instance.BONUS_STAR_END);
            },
            TweenLite.to(star.star, 30 * fs, {x : 0, y : 0, ease : Power3.easeIn}),
            TweenLite.to(star.star.scale, 30 * fs, {x : 1.3, y : 1.3})
        ]);
        tl.add([
            TweenLite.to(star.star.scale, 30 * fs, {x : 1.3, y : 1.3}),
            this._starAnimation.getAnimationAutoShowHide(true, true)
        ]);
        tl.add(() => {
            StarStruckGameModuleConfig.soundPlayer.switchTo(StarStruckSoundConfig.instance.MAIN_GAME_AMBIANCE, 0);
            Reels.getSymbol(1, 1)!.replaceWith("BWin", 1, StarStruckGameConfig.instance.SYMBOL_STATES.normal!);
            (<StarStruckReelSymbol>Reels.getSymbol(1, 1)!).addMultiplier(data.bonusSymbolWinMultiplier);
            this.reset();
            super.stopPresentation();
            closeCallback();
            this._starAnimation.hide();
            this._stars = [];
            this._buttons = [];

        });
        tl.play(0);
    }

    private onIdle():void {
        this._isPlaying = true;
        this.playIdleAnimation(this._startTime, false);
    }

    private playIdleAnimation(startTime:number, playBefore:boolean):void {
        if(!this._isPlaying) {
            return;
        }

        if(playBefore) {
            SlotGame.sound.playEffect(StarStruckSoundConfig.instance.BONUS_STAR_SWEEP);
            this._starsPopAnimation!.timeScale(2);
            this._starsPopAnimation!.play(0);
        }

        this._idleTimer = TweenLite.to(this, startTime, {onComplete : () => this.playIdleAnimation(MathHelper.randomNumberInRange(this._minInterval, this._maxInterval), true)});
    }

    private onIdleStop():void {
        this._isPlaying = false;

        if(this._idleTimer) {
            this._idleTimer.pause();
            this._idleTimer.kill();
            this._idleTimer = null;
        }

        if(this._starsPopAnimation && this._starsPopAnimation.isActive()) {
            this._starsPopAnimation.pause();
        }
        this._starsPopAnimation = null;
    }

    protected createBackground():PIXI.Graphics {
        const dimmer = new PIXI.Graphics();
        dimmer.alpha = 0.6;
        return dimmer;
    }

    protected onResize(data:IResizeData):void {
        super.onResize(data);
        const center:[number, number] = [this._screenWidth * 0.5, this._screenHeight * 0.5];

        this._isResizeDirty = false;

        this._bonusStarAnimation.position.set(center[0], center[1]);
        this._starAnimation.position.set(center[0], center[1]);
        this._pickText.position.set(center[0], center[1]);
        this._shuffleAnimation.position.set(center[0], center[1]);
        this._buttonsContainer.position.set(center[0], center[1]);

        (<PIXI.Graphics>this._background).clear();
        (<PIXI.Graphics>this._background).beginFill(0x000000, 0.8);
        (<PIXI.Graphics>this._background).drawRect(0, 0, this._screenWidth, this._screenHeight);
    }

    private reset():void {
        while(this._buttons.length > 0) {
            this._buttonsContainer.removeChild(this._buttons.pop()!);
            this._buttonsContainer.removeChild((this._stars.pop()!).text);
        }
    }

    public addDelayToStart():void {
        this._addDelay = true;
    }

    public resetDelay():void {
        this._addDelay = false;
    }

    protected onResizeButtons(resizeData:IResizeData):void {}

    public wait():void {}
}